import dayjs from "dayjs";

import { DayOfWeek, DaysOfWeek } from "../../../../models/DayOfWeek";
import {
  FacilityReportFacilitySnapshotData,
  FacilitySnapshotPost,
} from "../../../../models/FacilityReportData";
import { ScoreType, standardizeScoreType } from "../../../../utils/score";

export type SnapshotTranslate = (
  key: string,
  options?: Record<string, string | number>
) => string;

export const SNAPSHOT_TRANSLATION_PREFIX =
  "facilityReportsPage.facilityReportDetails.facilitySnapshotSection";

export const formatSnapshotDate = (date: string) =>
  dayjs(date).format("YYYY/MM/DD");

export const formatStars = (rating: number | null) =>
  Array.from({ length: 5 }, (_, i) => (i < (rating ?? 0) ? "★" : "☆")).join("");

export const getPostPhotoUrl = (post: FacilitySnapshotPost) =>
  post.media?.find((media) => media.mediaFormat === "PHOTO")?.googleUrl;

export const SOCIAL_PLATFORMS = [
  { key: "facebook", label: "f", color: "#1877f2" },
  { key: "instagram", label: "I", color: "#e1306c" },
  { key: "youtube", label: "Y", color: "#ff0000" },
  { key: "linkedin", label: "in", color: "#0a66c2" },
  { key: "pinterest", label: "P", color: "#e60023" },
  { key: "twitter", label: "X", color: "#000000" },
] as const;

/**
 * Snapshot fields copied raw from the database may be either JSON-encoded
 * strings or already-parsed objects.
 */
const parseRaw = (value: unknown): any => {
  if (!value) return null;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const HEALTH_SCORES: Record<ScoreType, { label: string; className: string }> =
  {
    [ScoreType.A2]: { label: "A+", className: "good" },
    [ScoreType.A1]: { label: "A", className: "good" },
    [ScoreType.B1]: { label: "B", className: "warning" },
    [ScoreType.C1]: { label: "C", className: "bad" },
    [ScoreType.D1]: { label: "D", className: "critical" },
  };

export const getHealthScoreDisplay = (healthIndex: string) =>
  HEALTH_SCORES[standardizeScoreType(healthIndex)];

export const getAddressLines = (
  snapshot: FacilityReportFacilitySnapshotData,
  postalPrefix: string
): string[] => {
  const address = snapshot.location_address;
  if (!address) {
    return snapshot.address ? [snapshot.address] : [];
  }

  const lines: string[] = [];
  if (address.postal_code) {
    lines.push(`${postalPrefix}${address.postal_code}`);
  }
  const street = [
    address.administrative_area,
    address.locality,
    address.sublocality,
    ...(address.address_lines ?? []),
  ]
    .filter(Boolean)
    .join("");
  if (street) {
    lines.push(street);
  } else if (address.formatted_address || snapshot.address) {
    lines.push((address.formatted_address || snapshot.address) as string);
  }
  return lines;
};

export const getPrimaryPhone = (
  snapshot: FacilityReportFacilitySnapshotData
): string | undefined =>
  parseRaw(snapshot.phone_numbers)?.primaryPhone || undefined;

const getOpeningDate = (
  snapshot: FacilityReportFacilitySnapshotData
): { year: number; month?: number; day?: number } | undefined => {
  const openingDate = parseRaw(snapshot.open_info)?.openingDate;
  return openingDate?.year ? openingDate : undefined;
};

export const formatOpeningDate = (
  snapshot: FacilityReportFacilitySnapshotData,
  t: SnapshotTranslate
) => {
  const openingDate = getOpeningDate(snapshot);
  if (!openingDate) return undefined;
  const { year, month, day } = openingDate;
  if (month && day) {
    return dayjs(new Date(year, month - 1, day)).format(t("date_format"));
  }
  if (month) {
    return dayjs(new Date(year, month - 1)).format(t("year_month_format"));
  }
  return t("year_format", { year });
};

interface ITimeOfDay {
  hours?: number;
  minutes?: number;
}

interface IApiTimePeriod {
  openDay: DayOfWeek;
  closeDay: DayOfWeek;
  openTime?: ITimeOfDay;
  closeTime?: ITimeOfDay;
}

const formatTime = (time?: ITimeOfDay) =>
  `${String(time?.hours ?? 0).padStart(2, "0")}:${String(
    time?.minutes ?? 0
  ).padStart(2, "0")}`;

/**
 * Returns the formatted opening periods for each day of the week, or
 * undefined when the facility has no regular hours registered.
 * A day with an empty array is closed.
 */
export const getRegularHoursByDay = (
  snapshot: FacilityReportFacilitySnapshotData
): { day: DayOfWeek; periods: string[] }[] | undefined => {
  const periods: IApiTimePeriod[] | undefined = parseRaw(
    snapshot.regular_hours
  )?.periods;
  if (!periods?.length) return undefined;

  return DaysOfWeek.map((day) => ({
    day,
    periods: periods
      .filter((period) => period.openDay === day)
      .map(
        (period) =>
          `${formatTime(period.openTime)}–${formatTime(period.closeTime)}`
      ),
  }));
};
